import React, { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

import { Loader2, MoreHorizontal } from "lucide-react";
import { User } from "@/types";
import Modal from "@/components/Modal";
import Form from "./Form";
import api from "@/lib/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import toast from "react-hot-toast";
import FormHeader from "@/components/FormHeader";
import { IoLogIn } from "react-icons/io5";
import { Input } from "@/components/ui/input";

const loginBypassMahasiswa = async (email: string) => {
  const response = await api.post("/auth/login-bypass-mahasiswa", {
    email,
  });

  return response?.data;
};

const verifikasiEmailUser = async (id: string) => {
  const response = await api.patch("/master/user/verifikasi-email", {
    id,
  });

  return response?.data;
};

const resetPasswordManual = async ({
  id,
  password,
}: {
  id: string;
  password: string;
}) => {
  const response = await api.patch("/master/user/reset-password-manual", {
    id,
    password,
  });

  return response?.data;
};

export default function Actions({ row }: { row: User }) {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showVerifikasiEmail, setShowVerifikasiEmail] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showConfirmationLoginBack, setShowConfirmationLoginBack] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState<User | null>(null);
  const [form, setForm] = useState({
    newPassword: "",
  });

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: loginBypassMahasiswa,
    onSuccess: (data) => {
      toast.success(data?.message || "Success.");
      window.location.assign("/");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateVerifikasiEmail, isPending: isPendingVerifikasiEmail } =
    useMutation({
      mutationFn: verifikasiEmailUser,
      onSuccess: (data) => {
        setShowVerifikasiEmail(false);
        queryClient.invalidateQueries({ queryKey: ["listUser"] });
        toast.success(data?.message || "Success.");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const { mutate: mutateResetPassword, isPending: isPendingResetPassword } =
    useMutation({
      mutationFn: resetPasswordManual,
      onSuccess: (data) => {
        setShowResetPassword(false);
        queryClient.invalidateQueries({ queryKey: ["listUser"] });
        toast.success(data?.message || "Success.");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  return (
    <React.Fragment>
      <div className="flex items-center gap-x-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-8 h-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setShowConfirmationLoginBack(true);
                setSelectedRow(row);
              }}
            >
              Loginback
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setShowEdit(true);
                setSelectedRow(row);
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setShowVerifikasiEmail(true);
                setSelectedRow(row);
              }}
            >
              Verifikasi Email
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setShowResetPassword(true);
                setSelectedRow(row);
              }}
            >
              Reset Password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setShowDelete(true);
                setSelectedRow(row);
              }}
            >
              Soft Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Modal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        className="w-[30vw]"
      >
        <Form
          closeModal={() => setShowEdit(false)}
          selectedRow={selectedRow}
          method="PATCH"
        />
      </Modal>

      <Modal show={showDelete} onClose={() => setShowDelete(false)}>
        <Form
          closeModal={() => setShowDelete(false)}
          selectedRow={selectedRow}
          method="DELETE"
        />
      </Modal>

      <Modal
        show={showConfirmationLoginBack}
        onClose={() => setShowConfirmationLoginBack(false)}
      >
        <FormHeader
          title={`Login sebagai ${selectedRow?.nama_lengkap}`}
          description={`Apakah anda yakin akan login sebagai ${selectedRow?.nama_lengkap}?`}
          icon={<IoLogIn />}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowConfirmationLoginBack(false)}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button
            variant={"default"}
            onClick={() => mutate(selectedRow?.email || "")}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Login"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        show={showVerifikasiEmail}
        onClose={() => setShowVerifikasiEmail(false)}
      >
        <FormHeader
          title={`Verifikasi email ${selectedRow?.email}`}
          description={`Apakah anda yakin akan memverifikasi email ${selectedRow?.email}?`}
          icon={<IoLogIn />}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowVerifikasiEmail(false)}
            disabled={isPendingVerifikasiEmail}
          >
            Batal
          </Button>
          <Button
            variant={"default"}
            onClick={() => mutateVerifikasiEmail(selectedRow?.id || "")}
            disabled={isPendingVerifikasiEmail}
          >
            {isPendingVerifikasiEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Verifikasi Email"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        show={showResetPassword}
        onClose={() => setShowResetPassword(false)}
      >
        <FormHeader
          title={`Reset password email ${selectedRow?.email}`}
          description={`Apakah anda yakin akan reset password ${selectedRow?.nama_lengkap}?`}
          icon={<IoLogIn />}
        />
        <div>
          <Input
            type="text"
            placeholder="Ketik password baru.."
            value={form.newPassword}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, newPassword: event.target.value }))
            }
          />
        </div>
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowResetPassword(false)}
            disabled={isPendingResetPassword}
          >
            Batal
          </Button>
          <Button
            variant={"default"}
            onClick={() =>
              mutateResetPassword({
                id: selectedRow?.id || "",
                password: form.newPassword,
              })
            }
            disabled={isPendingResetPassword}
          >
            {isPendingResetPassword ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Reset Password"
            )}
          </Button>
        </div>
      </Modal>
    </React.Fragment>
  );
}
